import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  TrendingUp,
  BarChart3,
  Zap,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth.context";
import { MaintenancePage } from "@/components/maintenance/MaintenancePage";
import {
  AVISO_DE_SESSAO_EXPIRADA,
  consumirMotivoDoLogoutAutomatico,
} from "@/lib/hey-api-client";

const AVISO_DO_LOGOUT_AUTOMATICO = {
  "sessao-expirada": AVISO_DE_SESSAO_EXPIRADA,
  "conta-inativa": {
    title: "Sua conta está inativa",
    description: "Fale com quem administra a sua conta para reativá-la.",
  },
} as const;

const LoginPage = () => {
  const navigate = useNavigate();
  const {
    login: authLogin,
    isMaintenanceMode,
    maintenanceMessage,
    checkMaintenanceStatus,
  } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  useEffect(() => {
    checkMaintenanceStatus();
  }, [checkMaintenanceStatus]);

  // O logout automático recarrega a página nesta tela; sem o aviso, o usuário só vê o
  // painel sumir.
  useEffect(() => {
    const motivo = consumirMotivoDoLogoutAutomatico();
    if (motivo) toast(AVISO_DO_LOGOUT_AUTOMATICO[motivo]);
  }, []);

  if (isMaintenanceMode) {
    return (
      <MaintenancePage
        message={maintenanceMessage}
        onRetry={checkMaintenanceStatus}
      />
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await authLogin(loginData.email, loginData.password);
    } catch (error) {
      console.error("Erro no login:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: TrendingUp,
      title: "Analytics Unificado",
      description:
        "Google Analytics, Meta Ads, Google Ads e muito mais em um só lugar.",
    },
    {
      icon: Zap,
      title: "IA Integrada",
      description:
        "Nossa Inteligência Artificial analisa seus dados e gera insights automáticos.",
    },
    {
      icon: BarChart3,
      title: "Decisões Inteligentes",
      description: "Aumente suas vendas com decisões baseadas em dados reais.",
    },
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Section - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2869C1]/20 to-[#9CCB3B]/10" />

        {/* Content */}
        <div className="relative z-10 space-y-10">
          {/* Logo + Title */}
          <div className="space-y-6">
            <img
              src="/logoSidebar.png"
              alt="Painel do E-Commerce"
              className="h-16"
            />
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white leading-tight">
                Transforme seus dados
                <br />
                <span className="text-[#9CCB3B]">em resultados reais</span>
              </h1>
              <p className="text-slate-300 text-lg max-w-md">
                Conecte todas suas fontes de dados e obtenha insights poderosos
                para escalar seu e-commerce.
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm transition-all hover:bg-white/10"
              >
                <div className="p-2 rounded-lg bg-[#9CCB3B]/20">
                  <feature.icon className="h-5 w-5 text-[#9CCB3B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-slate-400">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="text-slate-500 text-sm pt-8">
            © {new Date().getFullYear()} Painel do E-Commerce. Todos os direitos
            reservados.
          </div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logoSidebar.png"
              alt="Painel do E-Commerce"
              className="h-20 mx-auto"
            />
          </div>

          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900">
              Bem-vindo de volta
            </h2>
            <p className="mt-2 text-slate-600">
              Entre na sua conta para continuar
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-11 h-12 bg-white border-slate-200 focus:border-[#2869C1] focus:ring-[#2869C1] text-slate-900"
                    value={loginData.email}
                    onChange={(e) =>
                      setLoginData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">
                    Senha
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2869C1] hover:underline"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-11 pr-11 h-12 bg-white border-slate-200 focus:border-[#2869C1] focus:ring-[#2869C1] text-slate-900"
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-[#2869C1] hover:bg-[#1d5299]"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Entrar
                  <ArrowRight className="h-4 w-4" />
                </div>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-slate-50 text-slate-500">ou</span>
            </div>
          </div>

          {/* Register Link */}
          <div className="text-center">
            <p className="text-slate-600">
              Ainda não tem uma conta?{" "}
              <Link
                to="/register"
                className="font-semibold text-[#9CCB3B] hover:underline"
              >
                Criar conta grátis
              </Link>
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 pt-4 text-sm text-slate-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Dados seguros
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />7 dias grátis
            </div>
          </div>

          {/* Legal Links */}
          <div className="text-center text-xs text-slate-500 pt-2">
            <a
              href="https://www.paineldoecommerce.com.br/termos-de-uso/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#2869C1] hover:underline"
            >
              Termos de Uso
            </a>{" "}
            •{" "}
            <a
              href="https://www.paineldoecommerce.com.br/politica-de-privacidade/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#2869C1] hover:underline"
            >
              Política de Privacidade
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
